import React, { useState, useContext } from 'react';
import { Button, Typography, Divider, Alert } from 'antd';
import { ASSET_TYPE } from '@src/components/scene/react-three-components/sceneAsset';
import { ValidationResult } from '@src/utils/mml/types';
import { validateMML } from '@src/utils/mml/index';
import { convertThreeJSToMML } from '@src/utils/mml/reverseConverter';
import MMLEditor from './MMLEditor';
import styles from './mml-objects.module.css';
import { ThemeContext } from '../../../../pages/_app';

const { Title, Text } = Typography;

const EXAMPLES = {
  redCube: {
    code: '<m-cube id="my-cube" color="red"></m-cube>',
    name: 'Red Cube',
    description: 'A simple red cube',
  },
  greenSphere: {
    code: '<m-sphere id="my-sphere" color="green"></m-sphere>',
    name: 'Green Sphere',
    description: 'A simple green sphere',
  },
  cubeGroup: {
    code: '<m-group y="3" rz="45">\n  <m-cube x="-1.5" color="red"></m-cube>\n  <m-cube color="green"></m-cube>\n  <m-cube x="1.5" color="blue"></m-cube>\n</m-group>',
    name: 'Cube Group',
    description: 'A group of three cubes with different colors',
  },
  model: {
    code: '<m-model x="2" src="https://public.mml.io/duck.glb" sx="1.5" sy="1.5" sz="1.5" ry="-90"></m-model>',
    name: '3D Model',
    description: 'A 3D model loaded from a URL',
  },
  image: {
    code: '<m-image src="https://public.mml.io/test-image.jpg" y="2" width="5"></m-image>',
    name: 'Image',
    description: 'An image loaded from a URL',
  },
  video: {
    code: '<m-video src="https://public.mml.io/big-buck-bunny-10s-1mb.mp4" y="2" width="5" loop="true"></m-video>',
    name: 'Video',
    description: 'A video loaded from a URL',
  },
};

interface MMLObjectsProps {
  callback: (asset: any) => void;
  selectedModel?: any;
}

const MMLObjects: React.FC<MMLObjectsProps> = ({ callback, selectedModel }) => {
  const { isDark } = useContext(ThemeContext);
  const [mmlCode, setMmlCode] = useState<string>(EXAMPLES.redCube.code);
  const [validationResult, setValidationResult] =
    useState<ValidationResult | null>(null);
  const [objectName, setObjectName] = useState<string>('MML Object');

  const handleCodeChange = (newCode: string) => {
    setMmlCode(newCode);
  };

  const loadExample = (exampleKey: keyof typeof EXAMPLES) => {
    setMmlCode(EXAMPLES[exampleKey].code);
    setObjectName(EXAMPLES[exampleKey].name);
    setValidationResult(null);
  };

  const handleAddToScene = () => {
    const result = validateMML(mmlCode);
    setValidationResult(result);

    if (result && result.isValid) {
      callback({
        url: mmlCode,
        name: objectName,
        type: ASSET_TYPE.MML_OBJECT,
        ref: React.createRef(),
      });
    }
  };

  const handleThreeJSToMML = () => {
    if (!selectedModel) return;

    const mmlCode = convertThreeJSToMML(selectedModel);

    const threeObj = selectedModel.props?.asset?.ref?.current;

    setMmlCode(mmlCode);
    setObjectName(`Converted ${threeObj.name?.split('-')[0] || 'Object'}`);
    setValidationResult(null);
  };

  return (
    <div
      className={styles.container}
      data-cy='mml-objects-drawer'
      style={{ background: isDark ? '#141414' : '#fff' }}
    >
      <Title level={4} style={{ color: isDark ? '#fff' : undefined }}>
        MML Objects
      </Title>

      <Text strong style={{ color: isDark ? '#fff' : undefined }}>
        Examples:
      </Text>
      <div className={styles.exampleButtons}>
        <Button
          className={styles.exampleButton}
          onClick={() => loadExample('redCube')}
          data-cy='red-cube-button'
        >
          Red Cube
        </Button>

        <Button
          className={styles.exampleButton}
          onClick={() => loadExample('greenSphere')}
          data-cy='green-sphere-button'
        >
          Green Sphere
        </Button>
        <Button
          className={styles.exampleButton}
          onClick={() => loadExample('cubeGroup')}
          data-cy='cube-group-button'
        >
          Cube Group
        </Button>
        <Button
          className={styles.exampleButton}
          onClick={() => loadExample('model')}
          data-cy='3d-model-button'
        >
          3D Model
        </Button>
        <Button
          className={styles.exampleButton}
          onClick={() => loadExample('image')}
          data-cy='image-button'
        >
          Image
        </Button>
        <Button
          className={styles.exampleButton}
          onClick={() => loadExample('video')}
          data-cy='video-button'
        >
          Video
        </Button>
      </div>

      <Divider style={{ borderColor: isDark ? '#303030' : undefined }} />

      <MMLEditor
        initialValue={mmlCode}
        onChange={handleCodeChange}
        validationResult={validationResult}
      />

      <Button
        type='primary'
        onClick={handleAddToScene}
        className={styles.addButton}
        data-cy='add-to-scene-button'
      >
        Add to Scene
      </Button>

      {selectedModel && (
        <Button
          type='primary'
          onClick={handleThreeJSToMML}
          className={styles.addButton}
          style={{ marginTop: '8px' }}
        >
          Craft MML
        </Button>
      )}
    </div>
  );
};

export default MMLObjects;
